import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './ChatConsoleView.module.scss';
import ChatConsoleSidebar from 'component/molecules/chat-console-sidebar/ChatConsoleSidebar';
import { useBrandingContext } from 'app-context/BrandingsContext';
import { useUserContext } from 'app-context/user/UserContext';
import { usePost } from 'hooks/usePost';
import { chat, companyEndpoints } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import {
  createUserMessage,
  fixStreamUrl,
  transformChatData,
  transformNewChatData,
} from './factory/transformData';
import ChatBody from './components/chat-body/ChatBody';
import toast from 'react-hot-toast';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from 'component/layout/header/Header';
import Button from 'component/atom/forms/button/Button';
import axios from 'axios';
import { API_URL } from 'constants';
import { securityHeaders } from 'helpers/headers';
import { useAutoUpdate } from 'app-context/auto-update/AutoUpdate';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from './components/resizable-panel/Resizable';
import { useStream } from './factory/hook/useStream';

const ZERO_ID = '00000000-0000-0000-0000-000000000000';

const ChatConsoleView = ({
  isOpen,
  setIsOpen,
  hasSidebar = true,
  className,
  bodyClassName,
  chattingClassName,
  bot,
  inputContainerClass,
  isWorkFlow,
  isExpanded,
  triggerNewConversation,
  hasBotId,
  disableConsole,
  externalConversationId,
  hasHeader = true,
  ChatSource = 1,
}) => {
  const navigate = useNavigate();
  const { isActive } = useAutoUpdate();
  const { id } = useParams();

  const activeChatIdRef = useRef(null);
  const hasFetched = useRef(false);
  const initNewChatOnceRef = useRef(false);
  const creatingNewConvRef = useRef(false);
  const prevBotKeyRef = useRef(null);
  const streamAbortRef = useRef(null);

  const location = useLocation();
  const ExternalChatId =
    (!isWorkFlow && location.state?.chatId) || externalConversationId;
  const conversationId = id;
  const { userData } = useUserContext();
  const { brandingData } = useBrandingContext();

  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [idleTime, setIdleTime] = useState(0);
  const [guestChat, setGuestChat] = useState({ messages: [] });
  const [isChatting, setIsChatting] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFetchingChat, setIsFetchingChat] = useState(true);
  const [capture, setCapture] = useState();
  const [chatId, setChatId] = useState(() =>
    !isWorkFlow ? ExternalChatId : '' || ''
  );
  const [forceResetForm, setForceResetForm] = useState(false);
  const [restartForm, setRestartForm] = useState(false);
  const [isSubmitChatLoading, setIsSubmitChatLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const workflowClosed = isWorkFlow && !isExpanded;

  const { mutate: submitChat } = usePost();

  const currentBotKey = hasBotId ? conversationId : bot;

  const { data, refetch: refetchSettings } = useFetch(
    ['customMessageData', currentBotKey],
    companyEndpoints.getChatSettings,
    { enabled: true },
    { botId: currentBotKey }
  );

  const {
    data: chatsData,
    refetch: refetchChatsList,
    isFetching: isFetchingChatsList,
  } = useFetch(['chat-list', currentBotKey], chat.getChats(currentBotKey), {
    enabled: hasSidebar && !disableConsole && !isWorkFlow ? true : false,
  });

  const { refetch: refetchSingleChat } = useFetch(
    ['single-chat', hasBotId ? ExternalChatId : chatId],
    chat.getChatById(hasBotId ? ExternalChatId : chatId),
    { enabled: false }
  );

  const { ensureStreamingBotMessage, finalizeStreamingBotMessage, streamText } =
    useStream(setGuestChat);

  const filteredChats = useMemo(() => {
    return chatsData?.data?.filter((c) =>
      c.chatTitle.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, chatsData]);

  const fetchChat = useCallback(async () => {
    if (workflowClosed) return;
    const chatToFetch = ExternalChatId || chatId;
    if (!chatToFetch || chatToFetch === ZERO_ID) {
      setIsFetchingChat(false);
      return;
    }
    setIsFetchingChat(true);
    try {
      const res = await refetchSingleChat();
      const details = res?.data?.data?.chatUpdatedDetails;
      if (details && Object.keys(details).length > 0) {
        const chatData = transformNewChatData(details);
        setGuestChat((prev) => ({
          ...chatData,
          isFormSubmitted: true,
          chatID: prev.chatID || chatData.chatID,
        }));
        setCapture(data?.capture);
      }
    } catch (e) {
      console.error('Error fetching chat:', e);
    } finally {
      setIsFetchingChat(false);
    }
  }, [
    workflowClosed,
    ExternalChatId,
    chatId,
    refetchSingleChat,
    data?.capture,
  ]);
  const { mutateAsync: postGuestChat } = usePost();

  const startNewBotConversation = useCallback(async () => {
    if (workflowClosed) return;

    const botIdToUse = currentBotKey;
    if (!botIdToUse) {
      setIsFetchingChat(false);
      return;
    }

    if (creatingNewConvRef.current) return;
    creatingNewConvRef.current = true;
    setIsFetchingChat(true);

    if (chatId !== ZERO_ID) setChatId(ZERO_ID);

    const formData = new FormData();
    formData.append('ChatId', ZERO_ID);
    formData.append('BotId', botIdToUse);
    formData.append('ChatSource', String(ChatSource));

    try {
      const res = await postGuestChat({
        data: formData,
        url: chat.postGuestChat,
        isFormData: true,
      });

      const data = res?.data;
      setIdleTime(data?.idleTime);
      setCapture(data?.capture);
      setGuestChat(transformChatData(data));
      setSuggestedQuestions(data?.suggestedQuestions || []);
    } catch (err) {
      toast.error('Failed to start a new conversation.');
    } finally {
      creatingNewConvRef.current = false;
      setIsFetchingChat(false);
    }
  }, [workflowClosed, currentBotKey, chatId, ChatSource, postGuestChat]);

  const appendError = useCallback(() => {
    setGuestChat((prev) => {
      const updated = [...prev.messages];
      if (updated.length > 0) {
        const last = updated[updated.length - 1];
        const updatedContent = last.content.flatMap((it) => ({
          ...it,
          isError: true,
        }));
        updated[updated.length - 1] = { ...last, content: updatedContent };
      }
      return { ...prev, messages: updated };
    });
  }, []);

  const handleIdleSubmit = useCallback(() => {
    const formData = new FormData();
    formData.append('ChatId', guestChat.chatID);
    formData.append('BotId', currentBotKey || '');
    formData.append('IsIdle', 'true');
    formData.append('ChatSource', String(ChatSource));
    setIsSubmitChatLoading(true);
    submitChat(
      { data: formData, url: chat.postResponse, isFormData: true },
      {
        onSuccess: (res) => {
          if (res.data.suggestedQuestions) {
            setSuggestedQuestions(res.data.suggestedQuestions);
          }
          setGuestChat((prev) => ({
            ...prev,
            messages: [
              ...prev.messages,
              ...transformChatData(res.data).messages,
            ],
          }));
          setIsSubmitChatLoading(false);
        },
        onError: () => {
          setIsSubmitChatLoading(false);

          toast.error('Failed to send idle message.');
        },
      }
    );
  }, [guestChat?.chatID, currentBotKey, ChatSource, submitChat]);

  const handleSubmit = useCallback(
    async (values) => {
      if (isStreaming) return;
      try {
        if (
          !values.Question?.trim() &&
          (!values.images || values.images.length === 0) &&
          (!values.files || values.files.length === 0) &&
          !values.audio
        ) {
          return;
        }
        setIsSubmitChatLoading(true);
        const loadingMessage = {
          role: 'bot',
          content: [{ type: 'loading', value: '...' }],
          isLoading: true,
        };

        setForceResetForm((v) => !v);
        setIsChatting(true);
        const fixedQuestion = String(values.Question ?? '')
          .replace(/\+/g, '') // remove all plus signs
          .replace(/[\r\n]+/g, ' ') // new lines -> space
          .replace(/\s{2,}/g, ' ') // collapse extra spaces
          .trim();
        const effectiveChatId =
          chatId && chatId !== ZERO_ID ? chatId : guestChat?.chatID;

        const formData = new FormData();
        formData.append('ChatId', effectiveChatId || '');
        formData.append('BotId', currentBotKey || '');
        formData.append('ChatSource', String(ChatSource));
        formData.append('Question', fixedQuestion || '');

        if (values.sourceHandle) {
          formData.append('SourceHandle', values.sourceHandle);
        }
        values.images?.forEach((img) =>
          formData.append('Attachments', img.file)
        );
        values.files?.forEach((file) =>
          formData.append('Attachments', file.file)
        );
        if (values.audio && values.audio.file) {
          formData.append('Attachments', values.audio.file);
        }

        const userMessage = createUserMessage(values);

        setGuestChat((prev) => ({
          ...prev,
          messages: [
            ...prev.messages,
            { ...userMessage, workflowName: '' },
            loadingMessage,
          ],
        }));

        submitChat(
          { data: formData, url: chat.postResponse, isFormData: true },
          {
            onSuccess: async (res) => {
              if (
                activeChatIdRef.current === effectiveChatId ||
                activeChatIdRef.current === ZERO_ID
              ) {
                setGuestChat((prev) => {
                  const messages = [...prev.messages];
                  const lastUserIndex = messages.findIndex((msg, index) => {
                    return (
                      msg.role === 'user' &&
                      index === messages.findLastIndex((m) => m.role === 'user')
                    );
                  });
                  if (lastUserIndex !== -1) {
                    messages[lastUserIndex] = {
                      ...messages[lastUserIndex],
                      workflowName: res?.data?.workflowName || '',
                    };
                  }
                  setIsSubmitChatLoading(false);

                  return { ...prev, messages };
                });
              }

              const steps = Array.isArray(res?.data?.steps)
                ? res.data.steps
                : [];
              const responseAIStep =
                steps.find(
                  (s) => s?.nodeTypeId === 1 || s?.nodeType === 'Response AI'
                ) ||
                steps.find((s) =>
                  String(s?.name || '')
                    .toLowerCase()
                    .includes('response ai')
                );
              const streamUrl = responseAIStep?.message;

              if (streamUrl) {
                const fixedUrl = fixStreamUrl(streamUrl);
                const append = ensureStreamingBotMessage(
                  responseAIStep?.stepId || 'response-ai'
                );
                setIsStreaming(true);
                streamAbortRef.current = await streamText(fixedUrl, {
                  onChunk: (chunk) => {
                    if (
                      activeChatIdRef.current === effectiveChatId ||
                      activeChatIdRef.current === ZERO_ID
                    )
                      append(chunk);
                  },
                  onDone: () => {
                    if (
                      activeChatIdRef.current === effectiveChatId ||
                      activeChatIdRef.current === ZERO_ID
                    ) {
                      finalizeStreamingBotMessage();
                      if (hasSidebar) refetchChatsList();
                      streamAbortRef.current = null;
                      setIsStreaming(false);
                    }
                  },
                  onError: () => {
                    finalizeStreamingBotMessage();
                    setIsStreaming(false);
                    streamAbortRef.current = null;
                  },
                });

                setSuggestedQuestions(res?.data?.suggestedQuestions || []);
                return;
              }

              setGuestChat((prev) => {
                const messages = [...prev.messages];
                const updatedMessages = [
                  ...messages.filter((msg) => !msg.isLoading),
                  ...transformChatData(res.data).messages,
                ];
                return { ...prev, messages: updatedMessages };
              });

              setSuggestedQuestions(res?.data?.suggestedQuestions || []);
              if (hasSidebar) refetchChatsList();
            },
            onError: () => {
              setIsSubmitChatLoading(false);

              appendError();
              setGuestChat((prev) => ({
                ...prev,
                messages: prev.messages.filter((msg) => !msg.isLoading),
              }));
            },
          }
        );
      } catch {
        setIsSubmitChatLoading(false);

        toast.error('Unexpected error, please try again later');
      }
    },
    [
      chatId,
      guestChat?.chatID,
      currentBotKey,
      ChatSource,
      hasSidebar,
      refetchChatsList,
      submitChat,
      ensureStreamingBotMessage,
      finalizeStreamingBotMessage,
      streamText,
      appendError,
      isStreaming,
    ]
  );

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/Chat/DownloadChatConversations/${ExternalChatId}`,
        {
          responseType: 'blob',
          headers: securityHeaders(),
        }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'ChatConversation.pdf');
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed', error);
    }
  };

  const chatBodyProps = {
    isOpen,
    isFetchingChat,
    isChatting,
    isWorkFlow,
    guestChat,
    userData,
    hasSidebar,
    chatId: guestChat?.chatID,
    bot: currentBotKey,
    isSubmitChatLoading,
    capture,
    setCapture,
    handleSubmit,
    className,
    chattingClassName,
    inputContainerClass,
    bodyClassName,
    forceResetForm,
    setGuestChat,
    setSuggestedQuestions,
    suggestedQuestions,
    botSettingsData: data,
    isExpanded,
    restartForm,
    disableConsole,
    isConversation: ExternalChatId,
    idleTime,
    handleIdleSubmit,
    triggerNewConversation,
    isStreaming,
  };

  useEffect(() => {
    const key = currentBotKey || '';
    if (prevBotKeyRef.current !== key) {
      initNewChatOnceRef.current = false;
      hasFetched.current = false;
      prevBotKeyRef.current = key;
    }
  }, [currentBotKey]);

  useEffect(() => {
    refetchSettings();
  }, [isActive, refetchSettings]);

  useEffect(() => {
    refetchChatsList();
  }, [currentBotKey, refetchChatsList]);

  useEffect(() => {
    if (workflowClosed) {
      setIsFetchingChat(false);
      return;
    }
    if (initNewChatOnceRef.current) return;

    const activeChatId = ExternalChatId || chatId;
    const hasValidActive = !!activeChatId && activeChatId !== ZERO_ID;

    const shouldInit =
      location.pathname === '/chat-console' ||
      hasSidebar ||
      (!hasSidebar && isExpanded);
    if (!shouldInit) return;

    if (hasValidActive && !isWorkFlow) {
      fetchChat();
    } else {
      startNewBotConversation();
    }

    initNewChatOnceRef.current = true;

    if (!hasSidebar && isExpanded) {
      hasFetched.current = true;
    }
  }, [
    workflowClosed,
    ExternalChatId,
    chatId,
    location.pathname,
    hasSidebar,
    isExpanded,
    fetchChat,
    startNewBotConversation,
    isWorkFlow,
  ]);

  useEffect(() => {
    if (workflowClosed) return;
    if (!triggerNewConversation) return;

    startNewBotConversation();
    setRestartForm((v) => !v);
  }, [triggerNewConversation, workflowClosed, startNewBotConversation]);

  useEffect(() => {
    if (workflowClosed) return;
    if (!chatId || chatId === ZERO_ID) return;
    if (!initNewChatOnceRef.current && hasSidebar) return;
    fetchChat();
  }, [chatId, workflowClosed, hasSidebar, fetchChat]);

  useEffect(() => {
    return () => {
      if (streamAbortRef.current) {
        try {
          streamAbortRef.current();
        } catch {}
        streamAbortRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setIsSubmitChatLoading(false);
    if (streamAbortRef.current) {
      try {
        streamAbortRef.current();
      } catch {}
      streamAbortRef.current = null;
    }
    setIsStreaming(false);
  }, [chatId, ExternalChatId]);

  useEffect(() => {
    activeChatIdRef.current =
      ExternalChatId || chatId || guestChat?.chatID || null;
  }, [ExternalChatId, chatId, guestChat?.chatID]);
  return (
    <div
      key={bot}
      className={`${styles['chat-console']} ${
        isWorkFlow ? styles['workflow-layout'] : ''
      }`}
      style={{
        flexDirection: ExternalChatId ? 'column' : '',
        padding: ExternalChatId && !isWorkFlow ? '2rem' : '',
        paddingTop: ExternalChatId && !isWorkFlow ? '0rem' : '',
      }}
    >
      {ExternalChatId && hasHeader ? (
        <Button
          onClick={handleExport}
          className={styles['export-btn']}
          label='Export'
        />
      ) : null}

      {disableConsole && hasHeader && (
        <Header
          title='Conversation'
          isBack={true}
          onBackClick={() => navigate(-1)}
          isConversation={ExternalChatId}
          containerClassname={styles['conversation-header']}
        />
      )}

      {!isWorkFlow && !disableConsole ? (
        <ResizablePanelGroup direction='horizontal'>
          <ResizablePanel defaultSize={20}>
            {hasSidebar && !disableConsole && (
              <ChatConsoleSidebar
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                brandingData={brandingData}
                botId={currentBotKey}
                getActiveChat={(id) => {
                  if (id !== ZERO_ID) {
                    setChatId(id);
                    setForceResetForm((v) => !v);
                  }
                }}
                ChatId={guestChat?.chatID}
                chatsData={filteredChats}
                onNewChatClick={startNewBotConversation}
                onSearch={setSearchQuery}
                refetchChatsList={refetchChatsList}
                isLoading={isFetchingChatsList}
              />
            )}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel>
            <ChatBody {...chatBodyProps} disableConsole={disableConsole} />
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <ChatBody {...chatBodyProps} disableConsole={disableConsole} />
      )}
    </div>
  );
};

export default ChatConsoleView;
